import { ReactNode, useEffect } from 'react';
import { ColumnMeta, RowData } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'support/types/ui';
import { DiffStatus } from 'components/DataTable/types';

import { CellShell } from '../CellShell/CellShell';
import { deriveEditCellVariant } from '../cellVariant';

type InputWrapperCellProps = {
  fieldId: string;
  isHighlighted: boolean;
  children: ReactNode;
  className?: string;
  meta: ColumnMeta<RowData, unknown> | undefined;
  fieldState: FieldState;
  diffStatus?: DiffStatus;
};

export const InputWrapperCell = ({
  fieldId,
  isHighlighted,
  children,
  className,
  meta,
  fieldState,
  diffStatus,
}: InputWrapperCellProps) => {
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { service, NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext<WebEDIAnnotation>();
  const error = service.useItemByFieldPath(fieldId);
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const setCurrentItem = service.setCurrentItem;

  useEffect(() => {
    if (showNavigableItemPopover && error) {
      setCurrentItem(error);
    }
  }, [showNavigableItemPopover, error, setCurrentItem]);

  const variant = deriveEditCellVariant({ fieldState, diffStatus, isHighlighted });

  return (
    <>
      <CellShell
        variant={variant}
        mode="edit"
        isFocused={showNavigableItemPopover}
        fieldId={fieldId}
        className={className}
        onFocus={() => setShowNavigableItemPopover(true)}
        onBlur={() => setShowNavigableItemPopover(false)}
        ref={refs.setReference}
      >
        {children}
      </CellShell>
      {showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          annotation={error}
          viewMode={false}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={meta?.description}
        />
      )}
    </>
  );
};
